import { Fragment, h } from 'preact'
import compact from 'lodash-es/compact'
import isArray from 'lodash-es/isArray'
import isObject from 'lodash-es/isObject'
import fromPairs from 'lodash-es/fromPairs'

import { COMPONENTS } from '../transformers'

export default class Renderer {
  constructor() {
    this.COMPONENT_MAP = new Map()

    Object.entries(COMPONENTS)
      .forEach(entry => this.register(...entry))
  }

  register(name, Component) {
    this.COMPONENT_MAP.set(name, Component)
  }

  /**
   * @private
   */
  _getComponent(type) {
    return this.COMPONENT_MAP.get(type) || type
  }

  /**
   * @private
   */
  _mapValueToComponents(value) {
    if (value?.__isComponent__) {
      return this._renderComponent(value)
    }

    if (isArray(value)) {
      return value.map(this._mapValueToComponents.bind(this))
    }

    if (isObject(value)) {
      return this._mapPropsToComponents(value)
    }

    return value
  }

  /**
   * @private
   */
  _mapPropsToComponents(props = {}) {
    return fromPairs(
      Object.entries(props).map(([key, value]) => {
        value = this._mapValueToComponents(value)

        return [key, value]
      }),
    )
  }

  /**
   * @private
   */
  _renderComponent(c) {
    if (isArray(c)) {
      return c.map(this._renderComponent.bind(this))
    }

    if (!c || !c.__isComponent__) {
      return c
    }

    const {
      type = Fragment,
      children = [],
      props = {},
    } = c

    const Component = this._getComponent(type)
    const childComponents = this._renderTree(children)
    const computedProps = this._mapPropsToComponents(props)

    return h(Component, computedProps, ...childComponents)
  }

  /**
   * @private
   */
  _renderTree(tree = []) {
    if (!tree || !tree.length) {
      return []
    }

    return tree.map(c => this._renderComponent(c))
  }

  render(tree = []) {
    const components = compact([].concat(tree || []))

    const results = this._renderTree(components)

    if (results.length === 1) {
      return results[0]
    }

    return results
  }
}
